import { Button, Group, Paper, Stack, Textarea } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { salesOrderApi } from "apis/salesOrderApi";
import { Loading } from "components";
import dayjs from "configs/dayjs";
import {
  SalesOrderItemResponseType,
  SalesOrderResponseType,
  SalesOrderUpdateItemsBody,
  SOCreateFormValues,
} from "constants/types/salesOrder";
import { FormikProvider, useFormik } from "formik";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "utils/hooks";
import { useListInventory } from "utils/hooks/request/useInventories";
import SalesOrderItemsFieldArray from "pages/SalesOrder/pages/CreateSalesOrder/components/SalesOrderItemsFieldArray/SalesOrderItemsFieldArray";
import SalesOrderPartnerFields from "pages/SalesOrder/pages/CreateSalesOrder/components/SalesOrderPartnerFields/SalesOrderPartnerFields";
import {
  editItemsValidationSchema,
  mapResponseItemsToFormValues,
} from "pages/SalesOrder/pages/DetailSalesOrder/config";
import CustomText from "components/ui/CustomText/CustomText";

interface SalesOrderItemsEditFormProps {
  order: SalesOrderResponseType;
  items: SalesOrderItemResponseType[];
  mutate: () => void;
}

const SalesOrderItemsEditForm: FC<SalesOrderItemsEditFormProps> = ({
  order,
  items,
  mutate,
}) => {
  const navigate = useNavigate();
  const { notification } = useNotification();
  const { inventories } = useListInventory();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues: SOCreateFormValues = useMemo(
    () => ({
      code: order.code,
      warehouseId: order.warehouseId,
      notes: order.notes ?? "",
      oldDebt: order.oldDebt ?? 0,
      createdAt: order.createdAt,
      partner: {
        id: order.partnerId,
        name: order.partner?.name ?? "",
        phone: order.partner?.phone ?? "",
        taxCode: order.partner?.taxCode ?? "",
        // Nạp người phụ trách HIỆU LỰC: của đơn nếu có, không thì của đối tác —
        // để ô hiển thị đúng thứ đang áp dụng cho đơn này.
        contactPersonId:
          order.contactPersonId ?? order.partner?.contactPersonId ?? "",
        regionId: order.partner?.regionId ?? "",
        notes: order.partner?.notes ?? "",
        isActive: order.partner?.isActive ?? true,
      },
      items: mapResponseItemsToFormValues(items),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const formik = useFormik<SOCreateFormValues>({
    initialValues,
    validationSchema: editItemsValidationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const payload: SalesOrderUpdateItemsBody = {
          partner: {
            ...(values.partner.id ? { id: values.partner.id } : {}),
            name: values.partner.name,
            phone: values.partner.phone,
            taxCode: values.partner.taxCode,
            contactPersonId: values.partner.contactPersonId || undefined,
            regionId: values.partner.regionId,
            notes: values.partner.notes || undefined,
            isActive: values.partner.isActive,
          },
          createdAt: values.createdAt,
          // Gửi ở CẤP ĐƠN. Để trong khối partner thôi thì backend bỏ qua (đối
          // tác có sẵn được nạp theo id, mọi field khác bị lờ đi) nên bấm lưu
          // xong không thấy đổi gì.
          contactPersonId: values.partner.contactPersonId || undefined,
          notes: values.notes || undefined,
          ...(values.oldDebt ? { oldDebt: values.oldDebt } : {}),
          items: values.items.map(
            ({ rowKey: _rowKey, productSpec, ...item }) => ({
              ...item,
              ...(productSpec ? { productSpec } : {}),
            }),
          ),
        };
        const res = await salesOrderApi.updateItems(order.id, payload);
        if (res.data?.success) {
          notification.success({ title: "Cập nhật sản phẩm thành công" });
          mutate();
          navigate({ search: "" });
        } else {
          notification.error({
            title: "Cập nhật thất bại",
            message: res.data?.error?.message,
          });
        }
      } catch (err: any) {
        notification.error({
          title: "Cập nhật thất bại",
          message: err?.response?.data?.error?.message,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleCancel = () => navigate({ search: "" });

  return (
    <Loading variant="overlay" visible={isSubmitting}>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Stack gap="md">
            <Paper p="md" withBorder>
              <Stack gap="sm">
                <CustomText variant="section-title">Thông tin đơn hàng</CustomText>
                <DateTimePicker
                  label="Ngày tạo"
                  value={
                    formik.values.createdAt
                      ? dayjs.unix(formik.values.createdAt).toDate()
                      : null
                  }
                  onChange={(value) =>
                    formik.setFieldValue(
                      "createdAt",
                      value ? dayjs(value).unix() : undefined,
                    )
                  }
                />
                <Textarea
                  label="Ghi chú"
                  autosize
                  minRows={2}
                  {...formik.getFieldProps("notes")}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder>
              <SalesOrderPartnerFields formik={formik} />
            </Paper>

            <Paper p="md" withBorder>
              <SalesOrderItemsFieldArray formik={formik} inventories={inventories} />
            </Paper>

            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Hủy chỉnh sửa
              </Button>
              <Button type="submit" loading={isSubmitting}>
                Lưu thay đổi
              </Button>
            </Group>
          </Stack>
        </form>
      </FormikProvider>
    </Loading>
  );
};

export default SalesOrderItemsEditForm;
